/**
 * 表單欄位尾端的兩顆「外連」圖示按鈕(REQ-20260902006)。
 *
 * 客戶 / 廠商基本資料原本各自寫了一份(客戶是 🌐 / 📍 emoji、廠商是文字「開啟」),
 * 需求要求兩邊統一成圖示,所以抽成共用元件,行為沿用兩邊既有的決策:
 *
 * - **缺 protocol 時開啟前補 `https://`,但絕不回寫輸入框**(REQ-20260902004 §三)——
 *   受控 input 的推導值會吃掉使用者的按鍵,是這個 repo 踩過的雷。
 * - **地圖搜尋帶的是合併後、含郵遞區號的完整地址**(REQ-20260901004 §三)。
 * - **兩顆都不吃表單的唯讀狀態**:開網頁 / 開地圖是「看」,不是改資料。
 * - `window.open(url, '_blank', 'noopener,noreferrer')` 防 tabnabbing
 *   (REQ-20260421008 §6 起就是這樣)。
 *
 * 與舊版唯一的行為差異:**值是空的 / 不合法時改成 `disabled`,不再整顆消失**。
 * 舊的「空值就 return null」讓使用者在還沒填值時根本看不到按鈕,
 * 於是以為系統沒有這個功能 —— 這正是本次需求「新增一鍵開啟網頁」的由來
 * (客戶表單其實早就有這顆鈕,只是空值時藏起來了)。
 */

/** 兩顆共用的外框:沿用原本的 border / rounded / hover,加上 disabled 時轉灰 */
const buttonCls =
  'shrink-0 flex items-center justify-center border rounded px-2 py-1 text-sky-500 hover:bg-slate-100 disabled:border-slate-200 disabled:text-slate-300 disabled:hover:bg-white';

/** 線條地球儀(赤道 + 經線),顏色跟著 button 的 text-sky-500 走 */
function GlobeIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.4 2.5 3.7 5.5 3.7 9s-1.3 6.5-3.7 9c-2.4-2.5-3.7-5.5-3.7-9s1.3-6.5 3.7-9Z" />
    </svg>
  );
}

/** 實心地圖圖釘;中間的圓孔靠 `evenodd` 挖空(不是白色圓形,才不會壓到 hover 底色) */
function MapPinIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="currentColor"
      fillRule="evenodd"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />
    </svg>
  );
}

/**
 * 把輸入框的字轉成可開啟的網址;開不了(空值 / 解析失敗 / 不是 http(s))時回 `null`,
 * 由按鈕轉成 disabled。
 */
function toWebsiteUrl(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  } catch {
    return null;
  }
  return candidate;
}

/** 網站欄位尾端的地球儀鈕:新分頁開啟該網址 */
export function WebsiteOpenButton({ value }: { value: string | null | undefined }): JSX.Element {
  const url = toWebsiteUrl(value);
  return (
    <button
      type="button"
      onClick={() => {
        if (url !== null) window.open(url, '_blank', 'noopener,noreferrer');
      }}
      disabled={url === null}
      className={buttonCls}
      title="開啟網站新分頁"
      aria-label="開啟網站新分頁"
      data-testid="website-open-btn"
    >
      <GlobeIcon />
    </button>
  );
}

/** 地址欄位尾端的圖釘鈕:新分頁開 Google 地圖搜尋該地址(含郵遞區號) */
export function AddressMapButton({
  value,
  label,
}: {
  value: string | null | undefined;
  label: string;
}): JSX.Element {
  const trimmed = (value ?? '').trim();
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
  return (
    <button
      type="button"
      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
      disabled={trimmed === ''}
      className={buttonCls}
      title={`在 Google 地圖開啟${label}`}
      aria-label={`在 Google 地圖開啟${label}`}
      data-testid="address-map-btn"
    >
      <MapPinIcon />
    </button>
  );
}
